import React, { useState } from 'react';
import { useDroppable } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Plus, Edit2, Check, X, Play, Flag, GripVertical } from 'lucide-react';
import KanbanTicketCard from './KanbanTicketCard';

function KanbanColumn({ column, tickets, onColumnNameUpdate, onTicketClick, onAddTicket, isStartColumn, isEndColumn, customFields, isLastColumn, isBoardAdmin }) {
  const [isEditingName, setIsEditingName] = useState(false);
  const [editedName, setEditedName] = useState(column.name);
  const [visibleCount, setVisibleCount] = useState(20);

  const { setNodeRef } = useDroppable({
    id: column.column_id,
  });

  // Sortable wrapper for the entire column (drag by header handle) - only enabled for board admin
  const {
    attributes,
    listeners,
    setNodeRef: setSortableRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: `col:${column.column_id}`,
    disabled: !isBoardAdmin,
    data: { type: 'column', column_id: column.column_id },
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
  };

  const handleSaveName = () => {
    if (editedName.trim() && editedName !== column.name) {
      onColumnNameUpdate(column.column_id, editedName.trim());
    }
    setIsEditingName(false);
  };

  const handleCancelEdit = () => {
    setEditedName(column.name);
    setIsEditingName(false);
  };

  return (
    <div ref={setSortableRef} style={style} className="w-80 flex-shrink-0 flex flex-col" data-testid={`kanban-column-${column.column_id}`}>
      {/* Column Header */}
      <div className={`bg-white border rounded-xl p-4 mb-3 shadow-soft ${
        isStartColumn ? 'border-blue-300 ring-1 ring-blue-100' : 
        isEndColumn ? 'border-emerald-300 ring-1 ring-emerald-100' : 
        'border-stone-200'
      }`}>
        {isEditingName ? (
          <div className="flex items-center gap-2">
            <input
              type="text"
              value={editedName}
              onChange={(e) => setEditedName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleSaveName();
                if (e.key === 'Escape') handleCancelEdit();
              }}
              className="flex-1 px-2 py-1 border border-stone-300 rounded-lg text-sm font-medium text-stone-900 focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary"
              autoFocus
              data-testid="column-name-input"
            />
            <button
              onClick={handleSaveName}
              className="p-1.5 hover:bg-emerald-50 rounded-lg transition-colors"
              data-testid="save-column-name-button"
            >
              <Check className="w-4 h-4 text-emerald-600" strokeWidth={2} />
            </button>
            <button
              onClick={handleCancelEdit}
              className="p-1.5 hover:bg-stone-100 rounded-lg transition-colors"
              data-testid="cancel-column-name-button"
            >
              <X className="w-4 h-4 text-stone-600" strokeWidth={2} />
            </button>
          </div>
        ) : (
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              {isBoardAdmin && (
                <button
                  {...attributes}
                  {...listeners}
                  type="button"
                  aria-label="Drag to reorder column"
                  data-testid={`drag-column-handle-${column.column_id}`}
                  className="p-1 -ml-1 rounded hover:bg-stone-100 cursor-grab active:cursor-grabbing touch-none"
                  onClick={(e) => e.stopPropagation()}
                >
                  <GripVertical className="w-4 h-4 text-stone-400" strokeWidth={2} />
                </button>
              )}
              {isStartColumn && (
                <span className="p-1 bg-blue-100 rounded" title="TTC Start Column">
                  <Play className="w-3 h-3 text-blue-600" strokeWidth={2} />
                </span>
              )}
              {isEndColumn && (
                <span className="p-1 bg-emerald-100 rounded" title="TTC End Column">
                  <Flag className="w-3 h-3 text-emerald-600" strokeWidth={2} />
                </span>
              )}
              <h3 className="text-sm font-semibold text-stone-900 uppercase tracking-wider">
                {column.name}
              </h3>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-xs font-semibold text-primary bg-primary/10 px-2.5 py-1 rounded-full">
                {tickets.length}
              </span>
              <button
                onClick={() => setIsEditingName(true)}
                className="p-1.5 hover:bg-stone-100 rounded-lg transition-colors"
                data-testid="edit-column-name-button"
              >
                <Edit2 className="w-4 h-4 text-stone-400" strokeWidth={1.5} />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Droppable Area */}
      <div
        ref={setNodeRef}
        className={`flex-1 rounded-xl p-3 space-y-3 overflow-y-auto min-h-[200px] border ${
          isStartColumn ? 'bg-blue-50/30 border-blue-200/50' :
          isEndColumn ? 'bg-emerald-50/30 border-emerald-200/50' :
          'bg-stone-100/60 border-stone-200/50'
        }`}
      >
        <SortableContext items={(isLastColumn ? tickets.slice(0, visibleCount) : tickets).map((t) => t.ticket_id)} strategy={verticalListSortingStrategy}>
          {(isLastColumn ? tickets.slice(0, visibleCount) : tickets).map((ticket) => (
            <KanbanTicketCard
              key={ticket.ticket_id}
              ticket={ticket}
              customFields={customFields}
              onClick={() => onTicketClick(ticket)}
            />
          ))}
        </SortableContext>

        {/* Show More button for last column */}
        {isLastColumn && tickets.length > visibleCount && (
          <button
            onClick={() => setVisibleCount(prev => prev + 20)}
            data-testid="show-more-tickets-button"
            className="w-full py-2 px-3 bg-white border border-stone-200 rounded-xl text-sm text-stone-600 hover:bg-stone-50 hover:border-primary/30 transition-all font-medium"
          >
            Show More ({tickets.length - visibleCount} remaining)
          </button>
        )}

        {/* Add Ticket Button */}
        <button
          onClick={onAddTicket}
          data-testid={`add-ticket-to-column-${column.column_id}`}
          className="w-full py-2.5 px-3 border-2 border-dashed border-stone-300 rounded-xl text-sm text-stone-500 hover:border-primary hover:text-primary hover:bg-primary/5 transition-all"
        >
          <Plus className="w-4 h-4 inline mr-1" strokeWidth={2} />
          Add ticket
        </button>
      </div>
    </div>
  );
}

export default KanbanColumn;
